/**
 * Pure helpers and types for the milestone progress bars. Kept out of the
 * component files so they can be unit tested and so the component files
 * only export components (react-refresh requirement).
 */

export type MilestoneNodeColorOptions = 'blue' | 'green' | 'gold' | 'silver' | 'bronze'
export interface Milestone {
  /** Placement along the track, in value units (e.g. invites or dollars). */
  position: number
  /**
   * Value at which the node renders as "done". Defaults to `position`.
   * Start-of-track nodes (position 0) typically pass 1 so they only light
   * up once there is any progress.
   */
  completeAt?: number

  variant: 'initial-gift' | 'initial-invitation' | 'participation' | 'disney' | 'books' | 'medal' | 'goal'

  color?: MilestoneNodeColorOptions

  /**
   * Gradient of the track segment ending at this milestone, as CSS colors
   * (theme tokens encouraged). Omit for the default green gradient.
   */
  segmentGradient?: [from: string, to: string]
}

export const DEFAULT_SEGMENT_GRADIENT: [string, string] = ['var(--color-spring-green)', 'var(--color-leaf-green)']

/**
 * Fraction (0–1) of the way along the track for a given value, clamped to
 * the [0, max] range. Node centres and fill ends are both derived from this,
 * so they stay perfectly aligned. See MilestoneProgressBar for how the
 * fraction is mapped onto the full-width track (inset by the node radius so
 * the first/last nodes sit flush with the track ends without overflowing it).
 */
export function trackFraction(value: number, max: number): number {
  if (max <= 0) return 0
  return Math.max(0, Math.min(value / max, 1))
}

/** Percentage position (0–100) along the track for a given value. */
export function trackPosition(value: number, max: number): number {
  return trackFraction(value, max) * 100
}

/**
 * CSS `calc()` expression for a horizontal offset a given fraction (0–1)
 * along the track. The usable span is inset by the node radius (`--node-r`,
 * set on the track container) on both sides so nodes placed at fraction 0 or
 * 1 sit flush with the track ends without overflowing. Node centres and fill
 * ends share this helper so they always line up.
 */
export function nodeOffset(fraction: number): string {
  return `calc(var(--node-r) + (100% - 2 * var(--node-r)) * ${fraction})`
}

export interface FillSegment {
  /** Fraction (0–1) of the track where this segment starts. */
  startFrac: number
  /** Fraction (0–1) of the track where this segment ends. */
  endFrac: number
  gradient: [string, string]
}

/**
 * Computes the fill segments for the current value. The first segment grows
 * from the track's left edge; each later segment starts at the previous
 * milestone and only appears once the value passes it.
 */
export function fillSegments(milestones: Milestone[], value: number, max: number): FillSegment[] {
  if (value <= 0) return []

  const sorted = [...milestones].sort((a, b) => a.position - b.position)
  const segments: FillSegment[] = []

  for (const [i, milestone] of sorted.entries()) {
    const previous = sorted[i - 1]
    if (previous && value <= previous.position) break

    const startFrac = previous ? trackFraction(previous.position, max) : 0
    const endFrac = trackFraction(Math.min(value, milestone.position), max)
    segments.push({
      startFrac,
      endFrac: Math.max(startFrac, endFrac),
      gradient: milestone.segmentGradient ?? DEFAULT_SEGMENT_GRADIENT,
    })
  }

  return segments
}

export type PrizePhase = 'participation' | 'disney' | 'qualified'

/**
 * Which countdown the prize bar's bottom-right label shows: remaining
 * invites to the participation prize first, then to the Disney raffle,
 * then the qualified state once the Disney goal is reached.
 */
export function getPrizePhase(count: number, participationGoal: number, disneyGoal: number): PrizePhase {
  if (count >= disneyGoal) return 'qualified'
  if (count >= participationGoal) return 'disney'
  return 'participation'
}
